import { apiClient } from './apiClient';
import type { CommunicationChannel, ChannelType, ZernioConnectionStatus } from '../types/channel';
import { getChannelCategory, sanitizeZernioDisplayName } from '../utils/Constants';
import { Logger } from '../utils/logger';

// V4-FRONT-ANY-USAGE-BATCH-2 2026-05-28 — Backend shape returned by FastAPI
// `app/routes/channels.py`. Tous les champs sont optionnels parce que :
//  - le shape ES legacy peut omettre les champs Zernio (canaux créés avant
//    l'intégration)
//  - l'API PATCH renvoie l'objet complet mais avec des null-defaults variables
//  - `type` n'est jamais vide en pratique mais on garde l'optionnalité pour
//    rester tolérant aux payloads sortants d'autres endpoints.
interface BackendChannel {
  id: string;
  type: string; // valeur de ChannelType enum, en string côté wire (toujours présent dans la réponse backend)
  custom_name?: string;
  profile_name?: string;
  profile_url?: string;
  positioning?: string;
  date_from?: string;
  image_provider?: string;
  languages?: string[];
  enabled?: boolean;
  created_at?: string;
  zernio_account_id?: string;
  zernio_status?: ZernioConnectionStatus;
  zernio_platform?: string;
  zernio_username?: string;
  zernio_avatar_url?: string;
  token_expires_at?: string;
  health_status?: 'healthy' | 'warning' | 'error';
  purge_scheduled_at?: string;
}

type BackendChannelPayload = Record<string, unknown>;

export interface ImprovePositioningResponse {
  positioning: string;
  key_adaptations: string[];
}

/**
 * V4-API-DOUBLE-FETCH-CHANNELS — `useChannels` (mount effect) et
 * `useRaisoData.refreshData` (autre effect sur `currentSpace`) appellent tous
 * deux `channelsService.getAll(spaceId)` en parallèle au mount du dashboard.
 * Ce n'est PAS du StrictMode dev-only — ça se produit aussi en build prod.
 *
 * Plutôt que de coupler les deux hooks (ou supprimer un des call paths au
 * risque de casser le sync Zernio), on dédup au niveau service :
 *  - cache mémoire keyed par `spaceId` avec TTL court (30s)
 *  - un même `spaceId` en vol ⇒ on retourne la `Promise` déjà créée
 *
 * Effet : 2 appels concurrents au mount = 1 seule requête réseau. Les mutations
 * (create/update/delete) invalident le cache pour éviter de servir du stale.
 */
const CHANNELS_TTL_MS = 30_000;
type CachedChannels = { data: CommunicationChannel[]; expiresAt: number };
const channelsCache = new Map<string, CachedChannels>();
const inFlight = new Map<string, Promise<CommunicationChannel[]>>();

function mapChannel(item: BackendChannel): CommunicationChannel {
  return {
    id: item.id,
    channelType: getChannelCategory(item.type),
    network: item.type as ChannelType,
    customName: item.custom_name || '',
    profileName: sanitizeZernioDisplayName(item.profile_name) || '',
    profileUrl: item.profile_url || '',
    positioning: item.positioning || '',
    date_from: item.date_from || undefined,
    image_provider: item.image_provider || undefined,
    languages: item.languages && item.languages.length > 0 ? item.languages : ['fr'],
    enabled: item.enabled ?? true,
    createdDate: item.created_at ? new Date(item.created_at) : new Date(),
    zernioAccountId: item.zernio_account_id || undefined,
    zernioStatus: item.zernio_status || undefined,
    zernioPlatform: item.zernio_platform || undefined,
    zernioUsername: sanitizeZernioDisplayName(item.zernio_username),
    zernioAvatarUrl: item.zernio_avatar_url || undefined,
    tokenExpiresAt: item.token_expires_at || undefined,
    healthStatus: item.health_status || undefined,
    purgeScheduledAt: item.purge_scheduled_at || undefined,
  };
}

function invalidateChannelsCache(spaceId?: string) {
  if (spaceId) {
    channelsCache.delete(spaceId);
    inFlight.delete(spaceId);
  } else {
    channelsCache.clear();
    inFlight.clear();
  }
}

export const channelsService = {
  /**
   * Vide le cache mémoire. Utile pour les tests, ou si un consommateur
   * sait qu'il vient d'invalider l'état serveur d'une autre manière.
   */
  invalidateCache: invalidateChannelsCache,

  getAll: async (spaceId: string): Promise<CommunicationChannel[]> => {
    const now = Date.now();
    const cached = channelsCache.get(spaceId);
    if (cached && cached.expiresAt > now) {
      Logger.debug('[ChannelsService] Channels cache hit', { spaceId });
      return cached.data;
    }
    const pending = inFlight.get(spaceId);
    if (pending) {
      Logger.debug('[ChannelsService] Channels in-flight dedup', { spaceId });
      return pending;
    }

    Logger.debug('[ChannelsService] Fetching channels', { spaceId });
    const promise = apiClient
      .get<BackendChannel[]>('/channels/', {
        params: { space_id: spaceId },
        timeout: 60000,
      })
      .then((response) => {
        const channels = response.data.map(mapChannel);
        channelsCache.set(spaceId, { data: channels, expiresAt: Date.now() + CHANNELS_TTL_MS });
        Logger.info('[ChannelsService] Channels fetched', { spaceId, count: channels.length });
        return channels;
      })
      .finally(() => {
        inFlight.delete(spaceId);
      });

    inFlight.set(spaceId, promise);
    return promise;
  },

  create: async (spaceId: string, channelData: Partial<CommunicationChannel>): Promise<CommunicationChannel> => {
    Logger.info('[ChannelsService] Creating channel', {
      spaceId,
      network: channelData.network,
      channelType: channelData.channelType,
    });
    const payload: BackendChannelPayload = {
      space_id: spaceId,
      type: channelData.network,  // Already lowercase from enum
      custom_name: channelData.customName || '',
      profile_name: channelData.profileName || '',
      profile_url: channelData.profileUrl || '',
      positioning: channelData.positioning || '',
      languages: channelData.languages && channelData.languages.length > 0 ? channelData.languages : ['fr'],
      credentials: {},
      enabled: true
    };

    // Add optional fields if provided
    if (channelData.date_from) {
      payload.date_from = channelData.date_from;
    }
    if (channelData.image_provider) {
      payload.image_provider = channelData.image_provider;
    }

    // Timeout étendu pour la création de channel : le backend fait check_channel_limit + ES indexation
    const response = await apiClient.post<BackendChannel>('/channels/', payload, { timeout: 60000 });
    const createdChannel = {
      id: response.data.id,
      channelType: getChannelCategory(response.data.type),
      network: response.data.type as ChannelType,
      customName: response.data.custom_name || '',
      profileName: sanitizeZernioDisplayName(response.data.profile_name) || '',
      profileUrl: response.data.profile_url || '',
      positioning: response.data.positioning || '',
      date_from: response.data.date_from || undefined,
      image_provider: response.data.image_provider || undefined,
      languages: response.data.languages && response.data.languages.length > 0 ? response.data.languages : ['fr'],
      enabled: response.data.enabled ?? true,
      createdDate: response.data.created_at ? new Date(response.data.created_at) : new Date(),
      zernioAccountId: response.data.zernio_account_id || undefined,
      zernioStatus: response.data.zernio_status || undefined,
      zernioPlatform: response.data.zernio_platform || undefined,
      zernioUsername: sanitizeZernioDisplayName(response.data.zernio_username),
      zernioAvatarUrl: response.data.zernio_avatar_url || undefined,
    };
    invalidateChannelsCache(spaceId);
    Logger.info('[ChannelsService] Channel created', {
      channelId: createdChannel.id,
      spaceId,
      network: createdChannel.network,
    });
    return createdChannel;
  },

  update: async (id: string, channelData: Partial<CommunicationChannel>): Promise<CommunicationChannel> => {
    Logger.info('[ChannelsService] Updating channel', {
      channelId: id,
      hasNetwork: channelData.network !== undefined,
      hasCustomName: channelData.customName !== undefined,
      hasPositioning: channelData.positioning !== undefined,
      hasEnabled: channelData.enabled !== undefined,
    });
    const payload: BackendChannelPayload = {};
    if (channelData.network !== undefined) payload.type = channelData.network;
    if (channelData.customName !== undefined) payload.custom_name = channelData.customName;
    if (channelData.profileName !== undefined) payload.profile_name = channelData.profileName;
    if (channelData.profileUrl !== undefined) payload.profile_url = channelData.profileUrl;
    if (channelData.positioning !== undefined) payload.positioning = channelData.positioning;
    if (channelData.enabled !== undefined) payload.enabled = channelData.enabled;
    if (channelData.languages !== undefined) payload.languages = channelData.languages;

    const response = await apiClient.put<BackendChannel>(`/channels/${id}`, payload);
    const updatedChannel = {
      id: response.data.id,
      channelType: getChannelCategory(response.data.type),
      network: response.data.type as ChannelType,
      customName: response.data.custom_name || '',
      profileName: sanitizeZernioDisplayName(response.data.profile_name) || '',
      profileUrl: response.data.profile_url || '',
      positioning: response.data.positioning || '',
      languages: response.data.languages && response.data.languages.length > 0 ? response.data.languages : ['fr'],
      enabled: response.data.enabled ?? true,
      createdDate: response.data.created_at ? new Date(response.data.created_at) : new Date(),
      zernioAccountId: response.data.zernio_account_id || undefined,
      zernioStatus: response.data.zernio_status || undefined,
      zernioPlatform: response.data.zernio_platform || undefined,
      zernioUsername: sanitizeZernioDisplayName(response.data.zernio_username),
      zernioAvatarUrl: response.data.zernio_avatar_url || undefined,
    };
    // Cache space-keyed, on ne connaît pas le spaceId côté update — full clear.
    invalidateChannelsCache();
    Logger.info('[ChannelsService] Channel updated', {
      channelId: updatedChannel.id,
      network: updatedChannel.network,
    });
    return updatedChannel;
  },

  delete: async (id: string) => {
    Logger.warn('[ChannelsService] Deleting channel', { channelId: id });
    await apiClient.delete(`/channels/${id}`);
    invalidateChannelsCache();
    Logger.info('[ChannelsService] Channel deleted', { channelId: id });
    return { success: true };
  },

  // V4-CHANNELS-TEST-DEADCODE (2026-06-25) — `testConnection` supprimée : aucun
  // appelant + la route backend POST /channels/{id}/test (succès mocké) a été retirée.

  /**
   * Génère un positionnement spécifique pour un canal via IA
   */
  improvePositioning: async (channelId: string, spaceId: string): Promise<ImprovePositioningResponse> => {
    Logger.info('[ChannelsService] Improving channel positioning', { channelId, spaceId });
    const response = await apiClient.post<ImprovePositioningResponse>('/channels/improve-positioning', {
      channel_id: channelId,
      space_id: spaceId
    });
    Logger.info('[ChannelsService] Channel positioning improved', {
      channelId,
      spaceId,
      adaptationCount: response.data.key_adaptations?.length ?? 0,
    });
    return response.data;
  },

  /**
   * Génère un positionnement pour un nouveau canal (mode création)
   */
  generatePositioning: async (
    spaceId: string,
    channelType: string,
    network: string,
    profileName?: string
  ): Promise<ImprovePositioningResponse> => {
    Logger.info('[ChannelsService] Generating channel positioning', {
      spaceId,
      channelType,
      network,
      hasProfileName: !!profileName,
    });
    const response = await apiClient.post<ImprovePositioningResponse>('/channels/generate-positioning', {
      space_id: spaceId,
      channel_type: channelType,
      network: network,
      profile_name: profileName
    });
    Logger.info('[ChannelsService] Channel positioning generated', {
      spaceId,
      network,
      adaptationCount: response.data.key_adaptations?.length ?? 0,
    });
    return response.data;
  }
};